import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useParams } from 'react-router-dom'
import {
  Download,
  File,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Film,
  Mail,
  MessageSquare,
  Music,
  Presentation,
  Send,
} from 'lucide-react'
import Button from '../../components/Button'
import Card from '../../components/Card'
import FormRow from '../../components/FormRow'
import PageHeader from '../../components/PageHeader'
import Select from '../../components/Select'
import StatusBadge from '../../components/StatusBadge'
import useGoBack from '../../components/useGoBack'
import { EmptyState, ErrorState, LoadingState } from '../../components/States'
import { useToast } from '../../components/toastContext'
import { addComment, getReport, listStates, setReportState } from './incidentsService'
import { incidentsStrings as S } from './strings'
import './incidents.css'
import { serverText } from '../../lib/i18n'
import { extensionOfName, fileKind } from '../../lib/file'

const STATE_TONE = {
  New: 'primary',
  InProgress: 'warning',
  Completed: 'success',
  Closed: 'dark',
}

/** A label the screen knows, or the server's own for a state it has never heard of. */
const stateLabel = (id, states) => S.states[id] ?? states.find((s) => s.id === id)?.label ?? id

/**
 * One security report — its own screen, not a sheet.
 *
 * This is a triage surface: the reported message in full, the attachments, a comment
 * thread with a reply box, and the control that moves the report on. In a side sheet
 * it was 560px of column with a `pre` block scrolling inside it, over a list that was
 * doing nothing behind it. A page gives the message the width it needs and keeps the
 * thread beside the facts instead of a screen below them.
 *
 * THE ENUM STATE IS NOT ON THE DETAIL PAYLOAD — only the localised text (see
 * `getReport`). The list passes the row's own state through the router when it opens
 * this screen; a report reached by its URL matches the text back to a state instead.
 */
/** One glyph per kind of file, matching the colour `incidents.css` gives the same kind. */
const FILE_ICON = {
  image: FileImage,
  pdf: FileText,
  email: Mail,
  word: FileText,
  excel: FileSpreadsheet,
  presentation: Presentation,
  archive: FileArchive,
  video: Film,
  audio: Music,
  text: FileText,
  file: File,
}

/**
 * ONE ATTACHMENT, SHOWING WHAT IT IS.
 *
 * These were bare links in a column: a reporter's screenshot and the forwarded email it
 * arrived in read identically, and the only way to tell them apart was to download both.
 * A picture now shows itself at thumbnail size, and everything else gets the glyph and
 * the extension of its kind.
 *
 * The API sends `{ name, url }` and NO MIME type, so the name is all there is to go on —
 * hence `fileKind`. A name that claims to be an image but does not load falls back to the
 * icon rather than leaving a broken frame in the panel.
 */
function Attachment({ file }) {
  const kind = fileKind(file.name)
  const extension = extensionOfName(file.name)
  const [showImage, setShowImage] = useState(kind === 'image')
  const Icon = FILE_ICON[kind] ?? File

  return (
    <li>
      <a className="inc-files__item" href={file.url} download={file.name} rel="noreferrer">
        <span className="inc-files__thumb" data-kind={kind}>
          {showImage ? (
            <img
              src={file.url}
              alt={file.name}
              loading="lazy"
              onError={() => setShowImage(false)}
            />
          ) : (
            <Icon size={18} strokeWidth={1.75} aria-hidden="true" />
          )}
        </span>
        <span className="inc-files__meta">
          <bdi className="inc-files__name">{file.name}</bdi>
          {/* An extension is not translated - it is the file's own name for itself. */}
          {extension && <span className="inc-files__ext">{extension.toUpperCase()}</span>}
        </span>
      </a>
    </li>
  )
}

export default function ReportPage() {
  const { id } = useParams()
  const location = useLocation()
  const goBack = useGoBack('/security-reports')
  const toast = useToast()

  const [report, setReport] = useState(null)
  const [states, setStates] = useState([])
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  /** What the report is in now — the control's baseline, so Apply stays off until it moves. */
  const [current, setCurrent] = useState(location.state?.state ?? '')
  const [state, setState] = useState(location.state?.state ?? '')
  const [savingState, setSavingState] = useState(false)
  const [comment, setComment] = useState('')
  const [posting, setPosting] = useState(false)

  const load = useCallback(
    (signal) =>
      getReport(id, signal).then((data) => {
        setReport(data)
        return data
      }),
    [id],
  )

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setFailed(false)

    Promise.all([load(), listStates().catch(() => [])])
      .then(([data, loadedStates]) => {
        if (cancelled) return
        setStates(loadedStates)
        /* Opened by its own URL: no row came with it, so the state control is seeded by
           matching the localised text the payload does carry. */
        if (!location.state?.state) {
          const matched = loadedStates.find((item) => item.label === data.stateText)?.id ?? ''
          setCurrent(matched)
          setState(matched)
        }
        setLoading(false)
      })
      .catch(() => {
        if (cancelled) return
        setReport(null)
        setFailed(true)
        setLoading(false)
      })

    return () => {
      cancelled = true
    }
    // `location.state` is read once, on arrival — a re-read would fight the control.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load])

  const stateOptions = useMemo(
    () => states.map((item) => ({ value: item.id, label: stateLabel(item.id, states) })),
    [states],
  )

  const saveState = async () => {
    setSavingState(true)
    const result = await setReportState(id, state)
    setSavingState(false)
    if (!result.ok) {
      toast.error(result.message || S.actionFailed)
      return
    }
    toast.success(serverText(result.message) || S.stateSaved)
    setCurrent(state)
    load().catch(() => {})
  }

  const postComment = async () => {
    if (!comment.trim()) return
    setPosting(true)
    const result = await addComment(id, comment.trim())
    setPosting(false)
    if (!result.ok) {
      toast.error(result.message || S.actionFailed)
      return
    }
    toast.success(serverText(result.message) || S.commentAdded)
    setComment('')
    /* Re-read rather than push the new comment in by hand: the author and the timestamp are the
       server's, and guessing them puts a row on screen that differs from the stored one. */
    load().catch(() => {})
  }

  /* The reported message, saved as a file. The legacy does exactly this — a client-side Blob —
     and it is right to: the content is already in hand and there is no download endpoint. */
  const download = () => {
    const blob = new Blob([report.content], { type: 'text/plain;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `report-${report.number}.eml`
    link.click()
    URL.revokeObjectURL(url)
  }

  if (loading) {
    return (
      <>
        <PageHeader title={S.row.view} onBack={goBack} />
        <Card>
          <LoadingState />
        </Card>
      </>
    )
  }

  if (failed || !report) {
    return (
      <>
        <PageHeader title={S.row.view} onBack={goBack} />
        <Card>
          <ErrorState title={S.notFound} description={S.notFoundHint} />
        </Card>
      </>
    )
  }

  return (
    <>
      <PageHeader
        onBack={goBack}
        title={report.title}
        subtitle={`${S.columns.id} ${report.number}`}
        actions={
          report.canDownload && (
            <Button variant="ghost" onClick={download}>
              <Download size={16} strokeWidth={1.75} />
              {S.download}
            </Button>
          )
        }
      />

      <div className="inc-page">
        <div className="inc-page__main">
          <Card title={S.reportContent}>
            {/*
             * Shown as TEXT, deliberately.
             *
             * For a phishing report the API hands over the forwarded email's raw source with no
             * encoding at all — content written by whoever sent the phish. Rendering that as
             * markup would execute an attacker's HTML inside an admin's authenticated session.
             * Every other category arrives HTML-encoded, so text is also the honest rendering
             * there. `pre` keeps the header block readable either way.
             */}
            <pre className="inc-detail__content">{report.content || '—'}</pre>
          </Card>

          <Card title={S.comments}>
            {report.comments.length === 0 ? (
              <EmptyState icon={MessageSquare} title={S.noComments} />
            ) : (
              <ol className="inc-thread">
                {report.comments.map((entry) => (
                  <li key={entry.id} className="inc-thread__item">
                    <div className="inc-thread__head">
                      <bdi className="inc-thread__author">{entry.author}</bdi>
                      <span className="inc-thread__date" dir="ltr">
                        {entry.date}
                      </span>
                    </div>
                    <p className="inc-thread__body">
                      <bdi>{entry.body}</bdi>
                    </p>
                  </li>
                ))}
              </ol>
            )}

            <div className="inc-detail__reply">
              <textarea
                className="text-field"
                rows={3}
                value={comment}
                onChange={(event) => setComment(event.target.value)}
                placeholder={S.commentPlaceholder}
                aria-label={S.addComment}
              />
              <Button onClick={postComment} disabled={posting || !comment.trim()}>
                <Send size={15} strokeWidth={1.75} />
                {S.addComment}
              </Button>
            </div>
          </Card>
        </div>

        <div className="inc-page__side">
          <Card title={S.details}>
            <dl className="inc-detail__meta">
              <div>
                <dt>{S.columns.state}</dt>
                <dd>
                  <StatusBadge tone={STATE_TONE[current] ?? 'dark'}>
                    {stateLabel(current, states) || report.stateText}
                  </StatusBadge>
                </dd>
              </div>
              <div>
                <dt>{S.columns.reporter}</dt>
                <dd>
                  <bdi>{report.reporter}</bdi>
                </dd>
              </div>
              <div>
                <dt>{S.columns.category}</dt>
                <dd>
                  <bdi>
                    {report.category}
                    {report.subCategory ? ` — ${report.subCategory}` : ''}
                  </bdi>
                </dd>
              </div>
              <div>
                <dt>{S.columns.createdOn}</dt>
                <dd dir="ltr">{report.createdOn || '—'}</dd>
              </div>
              <div>
                <dt>{S.columns.lastUpdate}</dt>
                <dd dir="ltr">{report.lastUpdate || '—'}</dd>
              </div>
            </dl>

            {/* Moving a report on EMAILS ITS REPORTER — the handler queues a notification batch on
                every call. That is worth one line above the button rather than a surprise after. */}
            <FormRow label={S.changeState} hint={S.changeStateHint}>
              {(fieldId) => (
                <div className="inc-detail__state">
                  <Select id={fieldId} value={state} onChange={setState} options={stateOptions} />
                  <Button onClick={saveState} disabled={savingState || state === current}>
                    {S.apply}
                  </Button>
                </div>
              )}
            </FormRow>
          </Card>

          {report.attachments.length > 0 && (
            <Card title={S.attachments}>
              <ul className="inc-files">
                {report.attachments.map((file) => (
                  <Attachment key={file.url} file={file} />
                ))}
              </ul>
            </Card>
          )}
        </div>
      </div>
    </>
  )
}
